import { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronsUpDown, Plus, Search, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "../ui/drawer";

const IngredienteCombobox = ({
  ingredienti = [],
  categorie = [],
  value,
  onSelect,
  onCreateNew,
  placeholder = "Seleziona ingrediente...",
  testId = "ingrediente-combobox"
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  const selectedNome = useMemo(() => {
    if (!value) return "";
    const found = ingredienti.find(i => i.id === value);
    return found?.nome || "";
  }, [value, ingredienti]);

  const catMap = useMemo(() => {
    const m = {};
    categorie.forEach(c => { m[c.id] = c.nome; });
    return m;
  }, [categorie]);

  // Filtra e raggruppa
  const grouped = useMemo(() => {
    const filtered = search.trim()
      ? ingredienti.filter(i => i.nome?.toLowerCase().includes(search.toLowerCase()))
      : ingredienti;

    const groups = {};
    filtered.forEach(ing => {
      const catNome = catMap[ing.categoria_id] || "Altro";
      if (!groups[catNome]) groups[catNome] = [];
      groups[catNome].push(ing);
    });
    Object.values(groups).forEach(list => list.sort((a, b) => a.nome.localeCompare(b.nome, 'it')));
    const keys = Object.keys(groups).sort((a, b) => a === "Altro" ? 1 : b === "Altro" ? -1 : a.localeCompare(b, 'it'));
    return { groups, keys, total: filtered.length };
  }, [ingredienti, catMap, search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => searchRef.current?.focus(), 300);
    }
  }, [open]);

  return (
    <>
      {/* Trigger button */}
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="w-full h-12 rounded-xl justify-between font-normal text-left"
        data-testid={testId}
      >
        <span className={selectedNome ? "text-foreground truncate" : "text-muted-foreground"}>
          {selectedNome || placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {/* Drawer per selezione - mobile-friendly con scroll nativo */}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="max-h-[85vh]" data-testid={`${testId}-drawer`}>
          <DrawerHeader className="text-left pb-2">
            <DrawerTitle>Seleziona ingrediente</DrawerTitle>
            <DrawerDescription className="sr-only">Cerca l'ingrediente da associare al lotto.</DrawerDescription>
          </DrawerHeader>

          {/* Ricerca */}
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca ingrediente..."
                className="pl-10 h-12 text-base"
                data-testid={`${testId}-search`}
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1">
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {/* Lista scrollabile nativa */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
            style={{ WebkitOverflowScrolling: "touch", maxHeight: "55vh" }}
          >
            {grouped.total === 0 ? (
              <p className="text-center py-8 text-muted-foreground text-sm">Nessun ingrediente trovato</p>
            ) : (
              grouped.keys.map(catNome => (
                <div key={catNome} className="mb-2">
                  <div className="sticky top-0 bg-background/95 backdrop-blur-sm px-1 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider z-10">
                    {catNome} ({grouped.groups[catNome].length})
                  </div>
                  {grouped.groups[catNome].map(ing => (
                    <button
                      key={ing.id}
                      type="button"
                      onClick={() => { onSelect(ing.id); setOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-muted/50 active:bg-muted transition-colors"
                      data-testid={`ing-option-${ing.id}`}
                    >
                      {value === ing.id ? (
                        <div className="w-5 h-5 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3 w-3 text-black" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/20 flex-shrink-0" />
                      )}
                      <span className="font-medium text-sm flex-1">{ing.nome}</span>
                    </button>
                  ))}
                </div>
              ))
            )}

            {/* Crea nuovo */}
            {onCreateNew && (
              <button
                type="button"
                onClick={() => { onCreateNew(); setOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left text-primary font-medium text-sm mt-2 border border-primary/20 hover:bg-primary/5"
                data-testid={`${testId}-create-new`}
              >
                <Plus className="h-4 w-4" />
                Crea nuovo ingrediente
              </button>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default IngredienteCombobox;
